/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import get from 'lodash/fp/get.js';
import { APIProvider, Map, Marker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { Link } from 'react-router-dom';
import { buildingTypesById } from '#constants/buildingTypes/index.js';
import { occupancyLabelsById } from '#constants/occupancies.js';
import config, { GOOGLE_MAPS_API_KEY } from '#support/app/config.js';
import getMarkerIcon, { getMarkerCssColor } from './getMarkerColor.js';

const apiKey = config(GOOGLE_MAPS_API_KEY);

const METRIC_OPTIONS = [
  { value: 'sel', label: 'SEL' },
  { value: 'sul', label: 'SUL' },
];

const LEGEND_ITEMS = [
  { color: '#43a047', label: '< 5%' },
  { color: '#d4e157', label: '5–10%' },
  { color: '#fb8c00', label: '10–20%' },
  { color: '#e53935', label: '20–40%' },
  { color: '#b71c1c', label: '> 40%' },
  { color: '#9e9e9e', label: 'Not Specified' },
];

const getBuildingTypeLabel = id =>
  id && buildingTypesById[id] ? buildingTypesById[id].buildingType : id;
const getOccupancyLabel = id => (id != null ? occupancyLabelsById[id] || id : null);

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const DEFAULT_ZOOM = 4;

const formatPct = value => (value != null ? `${(value * 100).toFixed(0)}%` : '--');

type BoundsFitterProps = {|
  models: Array<{ lat: number, lng: number }>,
|};
const BoundsFitter = ({ models }: BoundsFitterProps): null => {
  const map = useMap();
  const fittedKeyRef = useRef<?string>(null);

  useEffect(() => {
    if (!map || !Array.isArray(models) || models.length === 0) return;
    // Only use models with valid lat/lng
    const validModels = models.filter(m => typeof m.lat === 'number' && typeof m.lng === 'number');
    if (validModels.length === 0) return;
    const key = validModels
      .map(m => `${m.lat},${m.lng}`)
      .sort()
      .join('|');
    if (fittedKeyRef.current === key) return;
    fittedKeyRef.current = key;

    const bounds = new window.google.maps.LatLngBounds();
    validModels.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, 60);
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > 14) map.setZoom(14);
    });
    // No return value needed for cleanup
    // eslint-disable-next-line consistent-return
    return undefined;
  }, [map, models]);
  return null;
};

type Model = {
  uuid: string,
  modelName: ?string,
  projectName: ?string,
  lat: ?number,
  lng: ?number,
  yearOfConstruction: ?number,
  designCodeYear: ?number,
  buildingTypeDir1: ?string,
  buildingTypeDir2: ?string,
  occupancy: ?string,
  numberOfStories: ?number,
  currentRun: ?{
    sel: ?number,
    sul: ?number,
  },
};

type Props = {|
  data: Model[],
  metric: string,
  onSetMetric: (metric: string) => void,
|};

const ModelsMap = ({ data, metric, onSetMetric }: Props): Element<'div'> => {
  const [activeGroupKey, setActiveGroupKey] = useState(null);

  // Only models with valid lat/lng
  const plottableModels = useMemo(
    () => data.filter(m => typeof m.lat === 'number' && typeof m.lng === 'number'),
    [data]
  );

  // Group models by exact lat/lng — key is "lat,lng"
  const locationGroups = useMemo(() => {
    const groups = {};
    plottableModels.forEach(m => {
      if (typeof m.lat !== 'number' || typeof m.lng !== 'number') return;
      const key = `${m.lat},${m.lng}`;
      if (!groups[key]) groups[key] = { lat: m.lat, lng: m.lng, models: [] };
      groups[key].models.push(m);
    });
    return groups;
  }, [plottableModels]);

  const activeGroup = activeGroupKey ? locationGroups[activeGroupKey] || null : null;

  return (
    <div>
      <div className="d-flex align-items-center mb-2 flex-wrap">
        <div className="d-flex align-items-center mr-4">
          <label className="mr-2 mb-0 font-weight-bold" htmlFor="map-metric-select">
            Colorize by:
            <select
              id="map-metric-select"
              className="form-control form-control-sm ml-2"
              style={{ width: 'auto' }}
              value={metric}
              onChange={e => onSetMetric(e.target.value)}
            >
              {METRIC_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="d-flex align-items-center small text-muted">
          {LEGEND_ITEMS.map(item => (
            <span key={item.label} className="d-flex align-items-center mr-3">
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  marginRight: 4,
                  flexShrink: 0,
                }}
              />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {plottableModels.length === 0 ? (
        <p className="text-muted">No models with location data to display.</p>
      ) : (
        <APIProvider apiKey={apiKey}>
          <Map
            style={{ width: '100%', height: '600px' }}
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            mapTypeId="satellite"
            onClick={() => setActiveGroupKey(null)}
          >
            <BoundsFitter
              models={plottableModels
                .filter(m => typeof m.lat === 'number' && typeof m.lng === 'number')
                .map(m => ({ lat: ((m.lat: any): number), lng: ((m.lng: any): number) }))}
            />
            {Object.entries(locationGroups).map(([key, group]) => {
              if (
                !group ||
                typeof group.lat !== 'number' ||
                typeof group.lng !== 'number' ||
                !Array.isArray(group.models)
              )
                return null;
              const typedGroup: { lat: number, lng: number, models: Array<Model> } = (group: any);
              const worstValue = typedGroup.models.reduce((worst, m) => {
                const v = get(`currentRun.${metric}`, m);
                if (v == null) return worst;
                return worst == null || v > worst ? v : worst;
              }, null);
              return (
                <Marker
                  key={key}
                  position={{ lat: typedGroup.lat, lng: typedGroup.lng }}
                  icon={getMarkerIcon(worstValue)}
                  onClick={() => setActiveGroupKey(key)}
                />
              );
            })}

            {activeGroup &&
              typeof activeGroup.lat === 'number' &&
              typeof activeGroup.lng === 'number' &&
              Array.isArray(activeGroup.models) && (
                <InfoWindow
                  position={{ lat: activeGroup.lat, lng: activeGroup.lng }}
                  onCloseClick={() => setActiveGroupKey(null)}
                  headerContent={
                    activeGroup.models.length === 1 ? (
                      <div style={{ fontWeight: 'bold', fontSize: 14 }}>
                        <Link to={activeGroup.models[0].uuid}>
                          {activeGroup.models[0].modelName || 'Unnamed Model'}
                        </Link>
                      </div>
                    ) : null
                  }
                >
                  <div style={{ minWidth: 180, fontSize: 13, maxHeight: 300, overflowY: 'auto' }}>
                    {activeGroup.models.map((model, i) => (
                      <div
                        key={model.uuid}
                        style={
                          i > 0
                            ? { borderTop: '1px solid #e0e0e0', paddingTop: 8, marginTop: 8 }
                            : {}
                        }
                      >
                        {activeGroup.models.length > 1 && (
                          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                            <Link to={model.uuid}>{model.modelName || 'Unnamed Model'}</Link>
                          </div>
                        )}
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                          <tbody>
                            {model.projectName && (
                              <tr>
                                <td
                                  style={{
                                    color: '#666',
                                    fontWeight: 'bold',
                                    paddingRight: 8,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Project:
                                </td>
                                <td>{model.projectName}</td>
                              </tr>
                            )}
                            {(model.buildingTypeDir1 || model.buildingTypeDir2) &&
                              (model.buildingTypeDir1 === model.buildingTypeDir2 ? (
                                <tr>
                                  <td
                                    style={{
                                      color: '#666',
                                      fontWeight: 'bold',
                                      paddingRight: 8,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Structural System:
                                  </td>
                                  <td>{getBuildingTypeLabel(model.buildingTypeDir1)}</td>
                                </tr>
                              ) : (
                                <tr>
                                  <td colSpan={2} style={{ paddingBottom: 2 }}>
                                    <div style={{ color: '#666', fontWeight: 'bold' }}>
                                      Structural System:
                                    </div>
                                    {model.buildingTypeDir1 && (
                                      <div style={{ paddingLeft: 10, marginTop: 2 }}>
                                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                                          Dir 1:{' '}
                                        </span>
                                        {getBuildingTypeLabel(model.buildingTypeDir1)}
                                      </div>
                                    )}
                                    {model.buildingTypeDir2 && (
                                      <div style={{ paddingLeft: 10, marginTop: 2 }}>
                                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                                          Dir 2:{' '}
                                        </span>
                                        {getBuildingTypeLabel(model.buildingTypeDir2)}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            {(model.yearOfConstruction != null || model.designCodeYear != null) && (
                              <tr>
                                <td
                                  style={{
                                    color: '#666',
                                    fontWeight: 'bold',
                                    paddingRight: 8,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {model.yearOfConstruction != null
                                    ? 'Year of Construction:'
                                    : 'Design Code Year:'}
                                </td>
                                <td>{model.yearOfConstruction ?? model.designCodeYear}</td>
                              </tr>
                            )}
                            {model.numberOfStories != null && (
                              <tr>
                                <td
                                  style={{
                                    color: '#666',
                                    fontWeight: 'bold',
                                    paddingRight: 8,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Stories:
                                </td>
                                <td>{model.numberOfStories}</td>
                              </tr>
                            )}
                            {model.occupancy != null && (
                              <tr>
                                <td
                                  style={{
                                    color: '#666',
                                    fontWeight: 'bold',
                                    paddingRight: 8,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Occupancy:
                                </td>
                                <td>{getOccupancyLabel(model.occupancy)}</td>
                              </tr>
                            )}
                            <tr>
                              <td
                                style={{
                                  color: '#666',
                                  fontWeight: 'bold',
                                  paddingRight: 8,
                                  paddingTop: 6,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                SEL:
                              </td>
                              <td style={{ paddingTop: 6 }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: getMarkerCssColor(
                                      get('currentRun.sel', model)
                                    ),
                                    marginRight: 4,
                                    verticalAlign: 'middle',
                                  }}
                                />
                                {formatPct(get('currentRun.sel', model))}
                              </td>
                            </tr>
                            <tr>
                              <td
                                style={{
                                  color: '#666',
                                  fontWeight: 'bold',
                                  paddingRight: 8,
                                  paddingTop: 4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                SUL:
                              </td>
                              <td style={{ paddingTop: 4 }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: getMarkerCssColor(
                                      get('currentRun.sul', model)
                                    ),
                                    marginRight: 4,
                                    verticalAlign: 'middle',
                                  }}
                                />
                                {formatPct(get('currentRun.sul', model))}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </InfoWindow>
              )}
          </Map>
        </APIProvider>
      )}
    </div>
  );
};

export default ModelsMap;
